import api from '../../services/api';

/*
 * The product master of the warehouse module.
 *
 * Two scopes the server keeps apart:
 *   listing     whoever works with that merchandise
 *   maintaining only the company that owns it, or the logistics operator that
 *               serves it — commercially, or by storing it in its centre
 *
 * There is no delete. A product with history in the kardex would leave
 * movements pointing at a SKU that no longer exists: it is taken out of
 * service with `is_active`.
 */

/**
 * @param {object} params - `company, search, is_active ('1'|'0'), page,
 *   page_size`. Each product carries `stock_total`, worked out from the kardex,
 *   and `components` when it is a kit.
 */
export const getCatalogRequest = async (params) => {
  return await api.get('products/catalog/', { params });
};

/**
 * @param {object} payload - `company_id` plus the product. The company is
 *   resolved by the server from that id and never read from anywhere else.
 */
export const createCatalogProductRequest = async (payload) => {
  return await api.post('products/catalog/create/', payload);
};

/** Only the fields sent are written, and the answer is the whole product. */
export const updateCatalogProductRequest = async (productId, payload) => {
  return await api.patch(`products/catalog/${productId}/`, payload);
};

/**
 * Bulk load for ONE company, declared here and not inside the sheet.
 *
 * An upsert — a SKU that exists is updated — and all or nothing: if one row
 * fails, none goes in.
 *
 * @param {FormData} formData - `company_id`, `excel_file`
 */
export const uploadCatalogRequest = async (formData) => {
  return await api.post('products/catalog/bulk/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * Products the single order form may add to an order: the active catalogue of
 * the seller's company, searched by SKU, name or barcode. No addon in front of
 * it, unlike `getCatalogRequest`. A seller account always gets its own
 * catalogue, whatever `sellerId` says.
 *
 * @param {object} params - `seller_id`, `search`
 * @returns {Promise<{data: {results: Array<object>, count: number}}>}
 */
export const getOrderFormProductsRequest = async (params) => {
  return await api.get('products/order_form_products/', { params });
};
